import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuthState } from "react-firebase-hooks/auth";
import {
  Copy,
  Download,
  Youtube,
  Image as ImageIcon,
  Link as LinkIcon,
  Clock,
  CheckCircle,
  AlertCircle,
  Monitor,
  LayoutGrid,
} from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import auth from "../../../../firebase.init";
import { deductUserCredit } from "../../../../utils/geminiUtils";

const YoutubeThumbnailDownloader = ({
  toolPath = "/user/yt-thumbnail-downloader",
}) => {
  // --- AUTH & CORE STATE (UNCHANGED) ---
  const [user, loadingAuth] = useAuthState(auth);
  const [loading, setLoading] = useState(true);
  const [loadingKeys, setLoadingKeys] = useState(true);
  const [loadingTools, setLoadingTools] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  // --- FORM STATE ---
  const [videoUrl, setVideoUrl] = useState("");
  const [showAllResolutions, setShowAllResolutions] = useState(true);

  // --- OUTPUT STATE ---
  const [resultData, setResultData] = useState(null);

  // --- CREDIT & TOOL CONFIG (UNCHANGED) ---

  const [creditCost, setCreditCost] = useState(2);
  const [apiKeys, setApiKeys] = useState([]);

  // Fetch API keys (Unchanged)
  const fetchApiKeys = async () => {
    try {
      const response = await axios.get(`https://allinoneai-d1a5a1b54c64.herokuapp.com/api/gemini-keys`);
      if (response.data && response.data.success && response.data.data) {
        const keys = response.data.data.map((keyObj) => keyObj.key);
        setApiKeys(keys);
        if (keys.length === 0)
          setError("No API keys available. Please contact administrator.");
      } else {
        setError("Failed to load API keys");
      }
    } catch (err) {
      console.error("Error fetching API keys:", err);
      setError("Error loading API keys");
    } finally {
      setLoadingKeys(false);
    }
  };

  // Fetch Tools List (Unchanged)
  const fetchToolsList = async () => {
    try {
      const response = await axios.get(
        `https://allinoneai-d1a5a1b54c64.herokuapp.com/api/tools-section`,
      );
      if (
        response.data &&
        response.data.success &&
        response.data.data &&
        response.data.data.tools
      ) {
        const tool = response.data.data.tools.find((t) => t.id === 11);
        if (tool) {
          setCreditCost(tool.creditCost);
        }
      }
    } catch (err) {
      console.warn("Could not fetch tools list, using default credit cost");
    } finally {
      setLoadingTools(false);
    }
  };

  // Initial Data Fetch (Unchanged)
  useEffect(() => {
    const fetchData = async () => {
      if (loadingAuth) return;
      if (!user) {
        setError("User not logged in");
        setLoading(false);
        setLoadingKeys(false);
        setLoadingTools(false);
        return;
      }
      try {
        await Promise.all([fetchApiKeys(), fetchToolsList()]);

        const userResponse = await axios.get(`https://allinoneai-d1a5a1b54c64.herokuapp.com/users`, {
          params: { userEmail: user.email },
        });
        if (userResponse.data && userResponse.data.length > 0) {
          const userData = userResponse.data[0];
        } else {
          setError("User data not found");
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, loadingAuth]);

  // Utility to extract Video ID safely
  const extractVideoId = (url) => {
    const regExp =
      /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(shorts\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[8].length === 11 ? match[8] : null;
  };

  // Generate content (Credit deduction logic exactly as requested)
  const extractThumbnails = async () => {
    if (!videoUrl.trim()) {
      setError("Please provide a YouTube Video URL.");
      return;
    }

    const videoId = extractVideoId(videoUrl);
    if (!videoId) {
      setError(
        "Invalid YouTube URL. Please provide a valid video or shorts link.",
      );
      return;
    }

    setGenerating(true);
    setError("");
    setResultData(null);

    try {
      // Deduct credits (Unchanged)

      // Attempt to fetch Video Metadata (Title & Channel) via public OEmbed
      let title = "Unknown Title";
      let channel = "Unknown Channel";

      try {
        const oembedRes = await fetch(
          `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`,
        );
        if (oembedRes.ok) {
          const oembedData = await oembedRes.json();
          title = oembedData.title || title;
          channel = oembedData.author_name || channel;
        }
      } catch (e) {
        console.warn(
          "Could not fetch metadata (CORS/Private). Proceeding with thumbnails only.",
        );
      }

      // Define standard YouTube thumbnail resolutions
      const resolutions = [
        {
          id: "maxresdefault",
          label: "Max Resolution",
          width: "1280",
          height: "720",
          quality: "HD",
        },
        {
          id: "sddefault",
          label: "Standard",
          width: "640",
          height: "480",
          quality: "SD",
        },
        {
          id: "hqdefault",
          label: "High",
          width: "480",
          height: "360",
          quality: "HQ",
        },
        {
          id: "mqdefault",
          label: "Medium",
          width: "320",
          height: "180",
          quality: "MQ",
        },
        {
          id: "default",
          label: "Default",
          width: "120",
          height: "90",
          quality: "LQ",
        },
      ];

      const thumbnails = resolutions.map((res) => ({
        ...res,
        url: `https://img.youtube.com/vi/${videoId}/${res.id}.jpg`,
      }));

      // Simulate a slight delay for better UX and processing time
      await new Promise((resolve) => setTimeout(resolve, 800));

      setResultData({
        videoId,
        title,
        channel,
        thumbnails,
      });

      toast.success("Thumbnails extracted successfully!");
    } catch (err) {
      console.error("Error extracting thumbnails:", err);
      setError(`Error: ${err.message}`);
      toast.error(`Error: ${err.message}`);
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = (text, label) => {
    if (!text) return;
    navigator.clipboard
      .writeText(text)
      .then(() => {
        toast.success(`${label} copied!`);
      })
      .catch(() => {
        toast.error(`Failed to copy ${label}`);
      });
  };

  // Safely download image by fetching it as a blob to bypass cross-origin restrictions
  const downloadImage = async (imgUrl, qualityLabel) => {
    try {
      const response = await fetch(imgUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `YouTube_Thumbnail_${qualityLabel.replace(/\s+/g, "_")}.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${qualityLabel} thumbnail!`);
    } catch (e) {
      console.warn("Direct download blocked, opening in new tab...");
      window.open(imgUrl, "_blank");
      toast.success("Opened image in new tab for download.");
    }
  };

  if (loadingAuth || loading || loadingKeys || loadingTools) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          background: "#f8fafc",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              border: "4px solid #f1f5f9",
              borderTop: "4px solid #ef4444",
              borderRadius: "50%",
              width: "40px",
              height: "40px",
              animation: "spin 1s linear infinite",
              margin: "0 auto 10px",
            }}
          ></div>
          <p style={{ color: "#64748b", fontWeight: "500" }}>
            Loading Workspace...
          </p>
        </div>

        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!user) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          background: "#f8fafc",
        }}
      >
        <div
          style={{
            background: "white",
            padding: "3rem",
            borderRadius: "16px",
            boxShadow: "0 10px 40px rgba(0,0,0,0.08)",
            textAlign: "center",
            maxWidth: "400px",
          }}
        >
          <ImageIcon
            size={48}
            color="#ef4444"
            style={{ marginBottom: "1rem" }}
          />
          <h2
            style={{
              color: "#0f172a",
              marginBottom: "1.5rem",
              fontSize: "1.5rem",
              fontWeight: "700",
            }}
          >
            Authentication Required
          </h2>
          <button
            onClick={() => (window.location.href = "/login")}
            style={{
              padding: "12px 24px",
              background: "linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              fontWeight: "600",
              width: "100%",
            }}
          >
            Proceed to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .thumb-app { font-family: 'Inter', system-ui, sans-serif; background: #f8fafc; min-height: 100vh; padding: 2rem 1rem; color: #334155; }
        
        /* Fixed SAAS Card - Removing height:100% and flex properties causing stretch */
        .saas-card { background: #ffffff; border-radius: 16px; box-shadow: 0 4px 24px rgba(0,0,0,0.03); border: 1px solid #e2e8f0; padding: 1.75rem; height: fit-content; }
        .saas-card-centered { background: transparent; padding: 6rem 2rem; border: 2px dashed #cbd5e1; border-radius: 16px; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; }
        
        .form-label { font-weight: 600; color: #475569; font-size: 0.9rem; margin-bottom: 0.5rem; display: block; }
        .form-control { width: 100%; padding: 0.85rem 1rem; border-radius: 10px; border: 1px solid #cbd5e1; background: #f8fafc; font-size: 0.95rem; color: #0f172a; transition: all 0.2s; margin-bottom: 1.25rem; box-sizing: border-box; }
        .form-control:focus { outline: none; border-color: #ef4444; box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1); background: #fff; }
        
        /* Fixed Gradient Button - Removing margin-top: auto */
        .gradient-btn { background: linear-gradient(135deg, #ef4444 0%, #b91c1c 100%); color: white; border: none; padding: 1rem 1.5rem; border-radius: 10px; font-weight: 600; font-size: 1rem; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 0.5rem; transition: all 0.3s ease; width: 100%; box-shadow: 0 4px 12px rgba(239, 68, 68, 0.25); margin-top: 0.5rem;}
        .gradient-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 6px 16px rgba(239, 68, 68, 0.35); }
        .gradient-btn:disabled { background: #cbd5e1; box-shadow: none; cursor: not-allowed; transform: none; }
        
        .toggle-switch { position: relative; display: inline-block; width: 44px; height: 24px; }
        .toggle-switch input { opacity: 0; width: 0; height: 0; }
        .slider { position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; background-color: #cbd5e1; transition: .4s; border-radius: 34px; }
        .slider:before { position: absolute; content: ""; height: 18px; width: 18px; left: 3px; bottom: 3px; background-color: white; transition: .4s; border-radius: 50%; }
        input:checked + .slider { background-color: #10b981; }
        input:checked + .slider:before { transform: translateX(20px); }

        .thumb-grid { display: grid; grid-template-columns: 1fr; gap: 1.5rem; margin-top: 1.5rem; }
        .thumb-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 12px rgba(0,0,0,0.02); transition: transform 0.2s, box-shadow 0.2s; }
        .thumb-card:hover { transform: translateY(-4px); box-shadow: 0 10px 24px rgba(0,0,0,0.06); border-color: #cbd5e1;}
        
        .thumb-img-container { width: 100%; background: #f1f5f9; display: flex; justify-content: center; align-items: center; position: relative; overflow: hidden; }
        .thumb-img { width: 100%; height: auto; display: block; object-fit: cover; }
        
        .thumb-info { padding: 1.25rem; display: flex; flex-direction: column; gap: 1rem; }
        .thumb-meta { display: flex; justify-content: space-between; align-items: center; }
        .thumb-title { font-size: 1.1rem; font-weight: 700; color: #0f172a; margin: 0; }
        .thumb-dims { font-size: 0.85rem; color: #64748b; font-weight: 500; background: #f1f5f9; padding: 4px 8px; border-radius: 6px; }
        
        .thumb-actions { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
        .action-btn { background: #fff; border: 1px solid #cbd5e1; color: #475569; padding: 10px; border-radius: 8px; font-size: 0.9rem; font-weight: 600; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 6px; transition: all 0.2s; }
        .action-btn.primary { background: #0f172a; color: #fff; border-color: #0f172a; }
        .action-btn:hover { background: #f8fafc; color: #0f172a; border-color: #94a3b8; }
        .action-btn.primary:hover { background: #1e293b; color: #fff; border-color: #1e293b; }
        
        @keyframes custom-pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.8; transform: scale(0.98); } }
        .anim-pulse { animation: custom-pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite; }
      `}</style>

      <div className="thumb-app">
        <div
          className="container"
          style={{ maxWidth: "1400px", margin: "0 auto" }}
        >
          {/* Top Navigation */}
          <div className="row mb-4">
            <div
              className="col-12 d-flex justify-content-between align-items-center"
              style={{ display: "flex", justifyContent: "space-between" }}
            >
              <button
                onClick={() => window.history.back()}
                style={{
                  background: "#fff",
                  border: "1px solid #e2e8f0",
                  padding: "8px 16px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontWeight: "600",
                  color: "#64748b",
                }}
              >
                ← Dashboard
              </button>
            </div>
          </div>

          {/* Header */}
          <div
            className="text-center mb-5"
            style={{ textAlign: "center", margin: "3rem 0" }}
          >
            <h1
              style={{
                fontSize: "2.5rem",
                fontWeight: "800",
                color: "#0f172a",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "12px",
                letterSpacing: "-0.02em",
              }}
            >
              <ImageIcon color="#ef4444" size={40} /> YouTube Thumbnail
              Downloader
            </h1>
            <p
              style={{
                color: "#64748b",
                marginTop: "12px",
                fontSize: "1.1rem",
                maxWidth: "650px",
                margin: "12px auto 0",
              }}
            >
              Extract and download high-quality thumbnails from any public
              YouTube video instantly.
            </p>
          </div>

          {error && (
            <div
              style={{
                background: "#fef2f2",
                color: "#ef4444",
                padding: "1rem",
                borderRadius: "10px",
                marginBottom: "2rem",
                textAlign: "center",
                border: "1px solid #fecaca",
                fontWeight: "600",
              }}
            >
              {error}
            </div>
          )}

          <div
            className="row"
            style={{ display: "flex", flexWrap: "wrap", gap: "2rem" }}
          >
            {/* Left Column - Form */}
            <div
              className="col-lg-4 col-md-5"
              style={{ flex: "1 1 350px", maxWidth: "450px" }}
            >
              <div className="saas-card">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "1.5rem",
                    paddingBottom: "1rem",
                    borderBottom: "1px solid #f1f5f9",
                  }}
                >
                  <LinkIcon size={20} color="#ef4444" />
                  <h3
                    style={{
                      margin: 0,
                      fontSize: "1.25rem",
                      color: "#0f172a",
                      fontWeight: "700",
                    }}
                  >
                    Extraction Settings
                  </h3>
                </div>

                <label className="form-label">
                  YouTube Video URL <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <div style={{ position: "relative" }}>
                  <Youtube
                    size={18}
                    color="#94a3b8"
                    style={{ position: "absolute", left: "12px", top: "14px" }}
                  />
                  <input
                    type="url"
                    className="form-control"
                    value={videoUrl}
                    onChange={(e) => setVideoUrl(e.target.value)}
                    placeholder="https://www.youtube.com/watch?v=..."
                    style={{ paddingLeft: "40px" }}
                  />
                </div>

                <div
                  style={{
                    background: "#f8fafc",
                    padding: "1.25rem",
                    borderRadius: "12px",
                    marginTop: "0.5rem",
                    marginBottom: "1.5rem",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <span
                        style={{
                          fontWeight: "600",
                          color: "#1e293b",
                          display: "block",
                          fontSize: "0.9rem",
                        }}
                      >
                        Show All Resolutions
                      </span>
                      <span style={{ fontSize: "0.75rem", color: "#64748b" }}>
                        Display all sizes (SD, HQ, Max)
                      </span>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={showAllResolutions}
                        onChange={(e) =>
                          setShowAllResolutions(e.target.checked)
                        }
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                </div>

                <button
                  onClick={extractThumbnails}
                  disabled={generating || !videoUrl.trim()}
                  className="gradient-btn"
                >
                  {generating ? (
                    <Clock size={18} className="fa-spin" />
                  ) : (
                    <Monitor size={18} />
                  )}
                  {generating ? "Extracting..." : `Extract Thumbnails `}
                </button>
              </div>
            </div>

            {/* Right Column - Results */}
            <div className="col-lg-8 col-md-7" style={{ flex: "1 1 500px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "1.5rem",
                }}
              >
                <h2
                  style={{
                    margin: 0,
                    fontSize: "1.5rem",
                    color: "#0f172a",
                    fontWeight: "700",
                  }}
                >
                  Available Thumbnails
                </h2>
              </div>

              {generating ? (
                <div className="saas-card-centered anim-pulse">
                  <div
                    style={{
                      width: "70px",
                      height: "70px",
                      background: "#fef2f2",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 1.5rem",
                    }}
                  >
                    <LayoutGrid size={34} color="#ef4444" />
                  </div>
                  <h3
                    style={{
                      color: "#0f172a",
                      fontWeight: "800",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Locating Assets...
                  </h3>
                  <p
                    style={{
                      color: "#64748b",
                      maxWidth: "350px",
                      margin: "0 auto",
                    }}
                  >
                    Parsing video ID and fetching all available image
                    resolutions.
                  </p>
                </div>
              ) : resultData ? (
                <div>
                  {/* Video Metadata Banner */}
                  <div
                    style={{
                      background: "#fff",
                      border: "1px solid #e2e8f0",
                      padding: "1.5rem",
                      borderRadius: "12px",
                      marginBottom: "1.5rem",
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "0.8rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        color: "#ef4444",
                        fontWeight: "700",
                      }}
                    >
                      Target Video
                    </span>
                    <h3
                      style={{
                        margin: 0,
                        fontSize: "1.25rem",
                        fontWeight: "800",
                        color: "#0f172a",
                      }}
                    >
                      {resultData.title}
                    </h3>
                    <p
                      style={{
                        margin: 0,
                        color: "#64748b",
                        fontSize: "0.95rem",
                      }}
                    >
                      By: <strong>{resultData.channel}</strong>
                    </p>
                  </div>

                  {/* Thumbnails Grid */}
                  <div className="thumb-grid">
                    {(showAllResolutions
                      ? resultData.thumbnails
                      : resultData.thumbnails.slice(0, 1)
                    ).map((thumb, index) => (
                      <div key={index} className="thumb-card">
                        <div className="thumb-img-container">
                          {/* Image element with error handling for non-existent resolutions (like missing maxres) */}
                          <img
                            src={thumb.url}
                            alt={`${thumb.label} Thumbnail`}
                            className="thumb-img"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src =
                                "https://via.placeholder.com/1280x720?text=Resolution+Unavailable";
                            }}
                          />
                        </div>

                        <div className="thumb-info">
                          <div className="thumb-meta">
                            <h4 className="thumb-title">
                              {thumb.label} ({thumb.quality})
                            </h4>
                            <span className="thumb-dims">
                              {thumb.width} x {thumb.height}
                            </span>
                          </div>

                          <div className="thumb-actions">
                            <button
                              className="action-btn primary"
                              onClick={() =>
                                downloadImage(thumb.url, thumb.label)
                              }
                            >
                              <Download size={16} /> Download
                            </button>
                            <button
                              className="action-btn"
                              onClick={() =>
                                copyToClipboard(thumb.url, "Image URL")
                              }
                            >
                              <Copy size={16} /> Copy URL
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="saas-card-centered">
                  <ImageIcon
                    size={56}
                    color="#cbd5e1"
                    style={{ marginBottom: "1.5rem" }}
                  />
                  <h3
                    style={{
                      color: "#64748b",
                      fontWeight: "700",
                      margin: "0 0 0.5rem 0",
                    }}
                  >
                    Awaiting URL
                  </h3>
                  <p style={{ color: "#94a3b8", margin: 0, maxWidth: "350px" }}>
                    Enter a YouTube video link to view and download its
                    thumbnails in multiple sizes.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default YoutubeThumbnailDownloader;
